import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { usePostHog } from 'posthog-js/react';
import { supabase, getRedirectURL } from '@/lib/supabase';
import { apiService } from '../services/api';
import { captureSupabaseAuthFailure } from '../utils/clientOperationAnalytics';
import { extractErrorMessage } from '../utils/errorUtils';
import { authDebugLog } from '../utils/authDebugLog';
import {
  captureLoginFailed,
  identifyByUserId,
  posthogPersonProperties,
} from '../utils/authProductEvents';
import {
  captureUserSignedUp,
  isAccountCreationAuthEvent,
  isSuccessfulAccountCreation,
} from '../utils/signupAnalytics';
import {
  clearCurrentProductEmail,
  withProductEmail,
} from '../utils/productEventEmail';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithMagicLink: (email: string) => Promise<void>;
  loginWithProvider: (provider: 'google') => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<void>;
  // CRITICAL FIX: Force fresh user data fetch from Supabase
  forceUserDataRefresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Supabase Auth Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const posthog = usePostHog();
  /** Dedup timestamps for auth-related PostHog events (stable across effect re-runs). */
  const recentEventTimesRef = useRef<Map<string, number>>(new Map());
  /**
   * False until `INITIAL_SESSION` from `onAuthStateChange` is processed. Supabase may emit
   * `SIGNED_IN` during hydration; we must not count that as a user-initiated sign-in.
   */
  const authBootstrapCompleteRef = useRef(false);
  /**
   * True after this page has applied a session with a user. Supabase also emits
   * SIGNED_OUT when a failed magic-link or OAuth redirect clears empty storage.
   * That is not a sign-out.
   */
  const hadAuthenticatedSessionRef = useRef(false);
  const signedInEmailRef = useRef<string | null>(null);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  const shouldFireDedupedEvent = (key: string, windowMs: number): boolean => {
    const last = recentEventTimesRef.current.get(key);
    const now = Date.now();
    if (!last || now - last > windowMs) {
      recentEventTimesRef.current.set(key, now);
      return true;
    }
    return false;
  };

  useEffect(() => {
    authBootstrapCompleteRef.current = false;

    const identifySession = (session: Session) => {
      if (!session.user) return;
      identifyByUserId(
        posthog,
        session.user.id,
        posthogPersonProperties(session.user),
      );
    };

    const isTerminalNoSessionEvent = (event: AuthChangeEvent) => {
      const e = event as string;
      return event === 'INITIAL_SESSION' || event === 'SIGNED_OUT' || e === 'USER_DELETED';
    };

    const applySignedOutUi = (event: AuthChangeEvent) => {
      const endedAuthenticatedSession = hadAuthenticatedSessionRef.current;
      const signedInEmail = signedInEmailRef.current;
      hadAuthenticatedSessionRef.current = false;
      signedInEmailRef.current = null;
      setSession(null);
      setUser(null);
      apiService.removeAuthToken();
      const e = event as string;
      // reset() clears first-touch UTMs. Only an authenticated session ending
      // (user sign-out, expiry, or deletion) should do that. A failed login
      // that clears empty state must not emit user_signed_out.
      if (
        posthog &&
        endedAuthenticatedSession &&
        (event === 'SIGNED_OUT' || e === 'USER_DELETED')
      ) {
        posthog.capture(
          'user_signed_out',
          withProductEmail({}, signedInEmail),
        );
        clearCurrentProductEmail();
        posthog.reset();
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      authDebugLog('onAuthStateChange', {
        event,
        hasSession: Boolean(session),
        userId: session?.user?.id,
        expiresAt: session?.expires_at,
      });

      if (!session) {
        if (!isTerminalNoSessionEvent(event)) {
          authDebugLog('ignoring null session for non-terminal event (keeping React auth state)', {
            event,
            bootstrapComplete: authBootstrapCompleteRef.current,
          });
          if (authBootstrapCompleteRef.current) {
            setIsLoading(false);
            return;
          }
        }
        applySignedOutUi(event);
        if (event === 'INITIAL_SESSION') {
          authBootstrapCompleteRef.current = true;
        }
        setIsLoading(false);
        return;
      }

      if (session.user) {
        hadAuthenticatedSessionRef.current = true;
        signedInEmailRef.current = session.user.email ?? null;
      }
      setSession(session);
      setUser(session.user);

      if (session.access_token) {
        apiService.setAuthToken(session.access_token);
      }

      if (session.user && posthog) {
        identifySession(session);

        if (event === 'INITIAL_SESSION' && session.user) {
          const sessionEventKey = `session_restored_${session.user.id}`;
          if (shouldFireDedupedEvent(sessionEventKey, 30000)) {
            if (import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true') {
              console.log('📊 Tracking session restoration event');
            }
            posthog.capture(
              'session_restored',
              withProductEmail(
                {
                  method: session.user.app_metadata?.provider || 'email',
                  session_duration: session.expires_at
                    ? Math.max(0, session.expires_at * 1000 - Date.now())
                    : null,
                },
                session.user.email,
              ),
            );
          } else if (import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true') {
            console.log('🚫 Deduped session_restored for PostHog');
          }
        }

        const accountJustCreated =
          isAccountCreationAuthEvent(event) && isSuccessfulAccountCreation(session.user);

        if (accountJustCreated) {
          captureUserSignedUp(posthog, {
            userId: session.user.id,
            method: session.user.app_metadata?.provider || 'email',
            email: session.user.email,
          });
        } else if (event === ('SIGNED_IN' as AuthChangeEvent)) {
          if (!authBootstrapCompleteRef.current) {
            if (import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true') {
              console.log('🔄 Skipping user_signed_in during auth bootstrap');
            }
          } else {
            const isReturningUser = Boolean(
              session.user.last_sign_in_at &&
                new Date(session.user.last_sign_in_at) < new Date(session.user.created_at)
            );
            const signinEventKey = `user_signed_in_${session.user.id}`;
            if (shouldFireDedupedEvent(signinEventKey, 30000)) {
              if (import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true') {
                console.log('📊 Tracking actual user sign-in event');
              }
              posthog.capture(
                'user_signed_in',
                withProductEmail(
                  {
                    method: session.user.app_metadata?.provider || 'email',
                    signin_timestamp: new Date().toISOString(),
                    is_returning_user: isReturningUser,
                  },
                  session.user.email,
                ),
              );
            } else if (import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true') {
              console.log('🚫 Deduped user_signed_in for PostHog');
            }
            if (isReturningUser) {
              localStorage.setItem('last_signin_timestamp', new Date().toISOString());
            }
          }
        } else if (event === ('TOKEN_REFRESHED' as AuthChangeEvent)) {
          if (import.meta.env.DEV || import.meta.env.VITE_ANALYTICS_DEBUG === 'true') {
            console.log('🔄 Token refreshed - user properties updated, no signin event');
          }
        }
      }

      if (event === 'INITIAL_SESSION') {
        authBootstrapCompleteRef.current = true;
      }

      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [posthog]);

  const loginWithMagicLink = useCallback(
    async (email: string) => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: getRedirectURL(),
        },
      });

      if (error) {
        captureLoginFailed(posthog, error);
        captureSupabaseAuthFailure(posthog, {
          surface: 'auth_context',
          operation: 'magic_link',
          error,
        });
        throw new Error(extractErrorMessage(error, 'Failed to send magic link'));
      }
    },
    [posthog]
  );

  const loginWithProvider = useCallback(
    async (provider: 'google') => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getRedirectURL(),
        },
      });

      if (error) {
        captureLoginFailed(posthog, error);
        captureSupabaseAuthFailure(posthog, {
          surface: 'auth_context',
          operation: 'oauth_google',
          error,
        });
        throw new Error(extractErrorMessage(error, 'Social login failed'));
      }
    },
    [posthog]
  );

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error logging out:', error);
      captureSupabaseAuthFailure(posthog, {
        surface: 'auth_context',
        operation: 'sign_out',
        error,
        severity: 'medium',
      });
    }
    apiService.removeAuthToken();
  }, [posthog]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 Refreshing Supabase session to get latest user metadata...');
      
      // CRITICAL FIX: Use getSession() first to check current session state
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        console.log('⚠️ No current session found - cannot refresh');
        captureSupabaseAuthFailure(posthog, {
          surface: 'auth_context',
          operation: 'refresh_session_no_session',
          error: new Error('No active session to refresh'),
          severity: 'medium',
        });
        throw new Error('No active session to refresh');
      }
      
      console.log('✅ Current session found, attempting refresh...');
      
      // Now that we have a session, try to refresh it
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Error refreshing session:', error);

        // If refresh fails but we have a current session, use that
        if (currentSession) {
          captureSupabaseAuthFailure(posthog, {
            surface: 'auth_context',
            operation: 'refresh_session_fallback',
            error,
            severity: 'medium',
          });
          console.log('🔄 Using current session as fallback');
          setSession(currentSession);
          setUser(currentSession.user);

          if (currentSession.access_token) {
            apiService.setAuthToken(currentSession.access_token);
          }
          return; // Don't throw error if we can use current session
        }

        captureSupabaseAuthFailure(posthog, {
          surface: 'auth_context',
          operation: 'refresh_session_failed',
          error,
        });
        throw error;
      }
      
      if (session) {
        console.log('✅ Session refreshed successfully');
        
        // Update local state with fresh session data
        setSession(session);
        setUser(session.user);
        
        // Update API service token
        if (session.access_token) {
          apiService.setAuthToken(session.access_token);
        }
        
        // Update PostHog with latest user metadata
        if (session.user) {
          identifyByUserId(
            posthog,
            session.user.id,
            posthogPersonProperties(session.user),
          );
          console.log(`User tier after refresh: ${session.user.user_metadata?.tier || 'free'}`);
        }
      } else {
        console.warn('⚠️ No session returned from refresh, using current session');
        // Use current session as fallback
        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          if (currentSession.access_token) {
            apiService.setAuthToken(currentSession.access_token);
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to refresh session:', error);
      throw error;
    }
  }, [posthog]);

  // CRITICAL FIX: Force fresh user data fetch from Supabase to handle stale JWT metadata
  const forceUserDataRefresh = useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 Force refreshing user data from Supabase...');

      // Get fresh session data directly from Supabase
      const { data: { session: freshSession } } = await supabase.auth.getSession();

      if (freshSession?.user) {
        const oldTier = userRef.current?.user_metadata?.tier || 'free';
        const newTier = freshSession.user.user_metadata?.tier || 'free';
        
        console.log(`🔍 Tier comparison: ${oldTier} → ${newTier}`);
        
        // Update local state with fresh user data
        setSession(freshSession);
        setUser(freshSession.user);
        
        // Update API token
        if (freshSession.access_token) {
          apiService.setAuthToken(freshSession.access_token);
        }
        
        // Update PostHog with fresh metadata
        if (freshSession.user) {
          identifyByUserId(
            posthog,
            freshSession.user.id,
            posthogPersonProperties(freshSession.user),
          );
          
          if (oldTier !== newTier && posthog) {
            posthog.capture(
              'tier_updated_in_session',
              withProductEmail(
                {
                  old_tier: oldTier,
                  new_tier: newTier,
                  method: 'force_refresh',
                },
                freshSession.user.email,
              ),
            );
          }
        }
        
        console.log(`✅ User data force refreshed - tier: ${newTier}`);
      } else {
        console.warn('⚠️ No session available for force refresh');
        throw new Error('No session available');
      }
    } catch (error) {
      console.error('❌ Failed to force refresh user data:', error);
      throw error;
    }
  }, [posthog]);

  const value = {
    user,
    session,
    isAuthenticated: !!user,
    isLoading,
    loginWithMagicLink,
    loginWithProvider,
    logout,
    getAccessToken,
    refreshSession,
    forceUserDataRefresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 